import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/context/AuthContext";
import { AUTH_CONFIRM, REGISTER_VERIFY } from "@/context/Api";

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [confirming, setConfirming] = useState(true);
  const [confirmed, setConfirmed] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const confirmAccount = async () => {
      try {
        const response = await AUTH_CONFIRM(token);
        if (response?.data?.status === false) {
          toast.error("Token not valid.");
          navigate("/user-panel");
          return;
        }
        setConfirmed(true);
      } catch (err) {
        toast.error(formatApiError(err?.detail) || formatApiError(err) || "Could not confirm account.");
        navigate("/");
      } finally {
        setConfirming(false);
      }
    };

    confirmAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetPassword = async (e) => {
    e.preventDefault();

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d?)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;

    if (!passwordRegex.test(password)) {
      toast.error(
        "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, and a special character."
      );
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("token", token);
      formData.append("password", password);
      formData.append("confirm_password", confirmPassword);

      await REGISTER_VERIFY(formData);
      toast.success("Password set successfully! You can now log in.");
      navigate("/user-panel");
    } catch (err) {
      toast.error(formatApiError(err?.detail) || formatApiError(err) || "Could not set password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07070c] text-white font-body">
      <Nav />
      <div className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 pt-24 pb-12 noise overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full blur-[130px] opacity-40 bg-[#4d4479]" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="set-password-card"
          className="relative z-10 w-full max-w-md mx-auto rounded-2xl border border-[#3d414f] bg-[#0a0a12]/90 backdrop-blur-xl p-6 sm:p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#16fcfb] text-[#07070c]">
              <KeyRound size={18} />
            </span>
            <span className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#16fcfb]">Set Password</span>
          </div>
          <h1 className="font-display font-700 text-3xl text-white">Secure your account</h1>

          {confirming ? (
            <p className="mt-4 flex items-center gap-2 font-body text-sm text-white/60">
              <Loader2 size={16} className="animate-spin" /> Confirming your account...
            </p>
          ) : confirmed ? (
            <p className="mt-2 font-body text-sm text-white/60">Account confirmed. Choose a password to continue.</p>
          ) : (
            <p className="mt-2 font-body text-sm text-red-400">
              We couldn't confirm your account. You can still try setting a password below.
            </p>
          )}

          <form onSubmit={handleSetPassword} className="mt-7 space-y-4" data-testid="set-password-form">
            <div className="space-y-1.5">
              <Label htmlFor="set-password" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="set-password"
                  data-testid="set-password-input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#16fcfb]"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="set-confirm-password" className="font-mono2 text-[10px] uppercase tracking-[0.12em] text-[#97b8ff]">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="set-confirm-password"
                  data-testid="set-confirm-password-input"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb] h-11 pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-confirm-password-visibility"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#16fcfb]"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="set-password-submit-btn"
              disabled={submitting}
              className="w-full rounded-full h-12 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest glow-cyan"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> Setting password
                </>
              ) : (
                "Set Password"
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default SetPassword;